import { MentaError, type MentaErrorCode } from './errors';
import { Emitter, type Unsubscribe } from './emitter';
import type { SiteNotAuthorizedReason, SiteStatus, SiteStatusGate } from './site-status';
import { mapWireErrorCode, type FieldGroup } from './field-group';
import type { InstallmentsError, InstallmentsResult } from './installments';
import type { InstallmentsFeed } from './installments-feed';
import type { CardFieldSession } from './session';
import type { FieldStyleByState, IframeTheme } from './transport/iframe';
import type { FieldName } from '../protocol';

/** Public event surface — frozen per the proposal's `CardFieldEvents`, plus
 * `resize` (design D3's escape hatch for container-sizing). No
 * `createCardToken` here: tokenization is a GROUP operation, exposed only via
 * `FieldsController.createCardToken()` / `menta.fields.createCardToken()`. */
export interface CardFieldEvents {
  error: (err: { code: MentaErrorCode; reason?: string }) => void;
  valid: (isValid: boolean) => void;
  ready: () => void;
  resize: (height: number) => void;
  /** Card-preview signal (Engram odd/card-preview/tasks): fires on every
   * keystroke with a digit count and, for the `pan` field only, a
   * PCI-capped BIN prefix (never the card value itself). Sourced from the
   * coordinator's `field-input` message, same as `valid`/`error` above. */
  input: (e: { length: number; bin?: string }) => void;
  /** Focus/blur (Engram odd/sdk-options/tasks, item 3). No payload — the
   * merchant only needs to know focus entered/left this field
   * (frontend-checkout uses CVV's to flip the card preview). Sourced from
   * the coordinator's `field-focus` message. */
  focus: () => void;
  blur: () => void;
  /** Per-field, derived signal (Engram odd/sdk-options/tasks, item A): no
   * new wire message — computed inside the SDK from the existing
   * `field-input` (`length` -> `empty`) and `field-state` (`valid` ->
   * `complete`) relays already flowing through `FieldGroup`. Fires only
   * when either value actually flips, same dedupe shape as `valid`. */
  change: (state: { empty: boolean; complete: boolean }) => void;
  /** `pan` only (no-op on `expiry`/`cvv`): installment plans for the card's
   * 8-digit BIN, fetched from api-checkouts when a NEW full BIN appears;
   * `null` when the BIN of the last emitted result goes away (shorter,
   * cleared or replaced). Nothing is requested until this has a listener.
   * See `InstallmentsFeed`. */
  installments: (result: InstallmentsResult | null) => void;
  /** `pan` only: the installments request for the current BIN failed.
   * Never fired for superseded/aborted requests. */
  installmentsError: (error: InstallmentsError) => void;
}

export interface CardFieldMountOptions {
  token: string;
  theme?: IframeTheme;
  locale?: string;
  /** Per-field placeholder/accessible-name override (Engram
   * odd/sdk-options/tasks, item 2). Constructor-level defaults
   * (`MentaOptions`) are NOT applied here — placeholder/ariaLabel are
   * inherently per-field text, unlike `locale`/`theme`. */
  placeholder?: string;
  ariaLabel?: string;
  /** Per-field `style`-by-state (Engram odd/sdk-options/tasks, item 5). */
  style?: FieldStyleByState;
}

function resolveContainer(target: string | HTMLElement): HTMLElement {
  if (typeof target !== 'string') {
    return target;
  }
  const element = document.querySelector(target);
  if (!element) {
    throw new MentaError('invalid_container', 'container not found');
  }
  return element as HTMLElement;
}

/** `CardFieldHandle` — public facade over one single-input
 * `CardFieldSession`, returned by `FieldsController.create()`. Holds no
 * state of its own; every call delegates. Unlike the pre-split `CardField`,
 * it has no `createCardToken()` — that is now `FieldsController`'s (via
 * `FieldGroup`) responsibility, since tokenizing requires all 3 fields plus
 * the coordinator. */
export class CardFieldHandle {
  private readonly siteEvents = new Emitter<{ notAuthorized: (reason: SiteNotAuthorizedReason) => void }>();
  /** Bumped by every `mount()` and by `unmount()`: a deferred mount that
   * finds a different value was superseded/cancelled and does nothing. */
  private mountSeq = 0;

  constructor(
    private readonly field: FieldName,
    private readonly session: CardFieldSession,
    private readonly group: FieldGroup,
    private readonly groupId: string,
    private readonly options: CardFieldMountOptions,
    private readonly siteStatus?: SiteStatusGate,
    /** Present only on the `pan` handle (see `FieldsController.create()`). */
    private readonly installments?: InstallmentsFeed,
  ) {}

  mount(target: string | HTMLElement): void {
    const container = resolveContainer(target);
    const seq = ++this.mountSeq;
    const gate = this.siteStatus;
    if (!gate) {
      this.insertIframe(container);
      return;
    }
    const apply = (outcome: SiteStatus): void => {
      if (seq !== this.mountSeq) return;
      if (outcome.status === 'not_authorized') {
        gate.errorNotAuthorizedOnce(outcome.reason);
        // Async so listeners registered right after `mount()` still hear it.
        queueMicrotask(() => {
          if (seq === this.mountSeq) this.siteEvents.emit('notAuthorized', outcome.reason);
        });
        return;
      }
      if (outcome.status === 'unavailable') gate.warnUnavailableOnce();
      this.insertIframe(container);
    };
    const current = gate.peek();
    if (current) apply(current);
    else void gate.whenSettled().then(apply);
  }

  private insertIframe(container: HTMLElement): void {
    this.session.mount(container, {
      route: this.field,
      group: this.groupId,
      token: this.options.token,
      theme: this.options.theme,
      locale: this.options.locale,
      placeholder: this.options.placeholder,
      ariaLabel: this.options.ariaLabel,
      style: this.options.style,
    });
  }

  on<K extends keyof CardFieldEvents>(event: K, callback: CardFieldEvents[K]): Unsubscribe {
    // `valid`/`error` come from the coordinator's `field-state` message
    // (routed through the group, filtered to this field), NOT from this
    // field's own `CardFieldSession` — the coordinator is the only party
    // that observes validity, since raw values never leave the same-origin
    // BroadcastChannel (see FieldGroupEvents.fieldValid doc comment).
    // `ready`/`resize` are genuinely per-iframe and stay on the session.
    if (event === 'valid') {
      const cb = callback as CardFieldEvents['valid'];
      return this.group.on('fieldValid', (field, valid) => {
        if (field === this.field) cb(valid);
      });
    }
    if (event === 'error') {
      const cb = callback as CardFieldEvents['error'];
      // Bug fix (Engram odd/sdk-options/tasks): `valid: false` with no
      // `code` (e.g. an untouched/empty field re-evaluated on a sibling
      // field's brand change) must stay silent — the wire contract says
      // `code` drives the displayed message, so no code means no visible
      // error yet.
      const unsubValid = this.group.on('fieldValid', (field, valid, code) => {
        if (field === this.field && !valid && code !== undefined) cb({ code: mapWireErrorCode(code) });
      });
      // Group-level: the coordinator handshake never completed. Every
      // handle hears it, since none of the fields is usable.
      const unsubHandshake = this.group.on('handshakeFailed', () => cb({ code: 'handshake_failed' }));
      const unsubSite = this.siteEvents.on('notAuthorized', (reason) =>
        cb({ code: 'site_not_authorized', reason }),
      );
      return () => {
        unsubValid();
        unsubHandshake();
        unsubSite();
      };
    }
    if (event === 'input') {
      const cb = callback as CardFieldEvents['input'];
      return this.group.on('fieldInput', (field, length, bin) => {
        if (field !== this.field) return;
        cb(bin !== undefined ? { length, bin } : { length });
      });
    }
    if (event === 'focus' || event === 'blur') {
      const cb = callback as CardFieldEvents['focus'] | CardFieldEvents['blur'];
      const wantFocused = event === 'focus';
      return this.group.on('fieldFocus', (field, focused) => {
        if (field === this.field && focused === wantFocused) cb();
      });
    }
    if (event === 'change') {
      const cb = callback as CardFieldEvents['change'];
      // No new wire message (item A): derived from the existing
      // `fieldInput`/`fieldValid` relays already flowing through
      // `FieldGroup`. `empty` starts `true`/`complete` starts `false`
      // (mirrors an unmounted-or-untouched field) and each one is only
      // re-emitted when IT flips, carrying the other's last known value.
      let empty = true;
      let complete = false;
      const unsubInput = this.group.on('fieldInput', (field, length) => {
        if (field !== this.field) return;
        const nextEmpty = length === 0;
        if (nextEmpty === empty) return;
        empty = nextEmpty;
        cb({ empty, complete });
      });
      const unsubValid = this.group.on('fieldValid', (field, valid) => {
        if (field !== this.field) return;
        if (valid === complete) return;
        complete = valid;
        cb({ empty, complete });
      });
      return () => {
        unsubInput();
        unsubValid();
      };
    }
    if (event === 'installments') {
      return this.installments?.onResult(callback as CardFieldEvents['installments']) ?? (() => {});
    }
    if (event === 'installmentsError') {
      return this.installments?.onError(callback as CardFieldEvents['installmentsError']) ?? (() => {});
    }
    return this.session.on(event, callback as never);
  }

  /** Focuses the input inside this field's iframe (Engram
   * odd/sdk-options/tasks, item B). Routed parent -> coordinator ->
   * (authorized) field channel -> field iframe, same path as
   * `clear()` — field iframes never act on a raw parent
   * `postMessage`. NOTE: some browsers restrict or delay programmatic
   * `HTMLElement.focus()` inside a cross-origin iframe unless the call
   * happens within a user-activation window; `src/inputs` still issues the
   * call, but it may be a no-op in that case (see `docs/integration.md`). */
  focus(): void {
    this.group.sendFieldCommand(this.field, 'focus');
  }

  /** Empties this field's input and resets its wire-visible state to
   * `field-input{length:0}` / `field-state{valid:false}` with no `code` —
   * consistent with the untouched-field rule (`CardFieldHandle.on('error')`
   * above), so `change`/error listeners and any card-preview UI stay in
   * sync (Engram odd/sdk-options/tasks, item B). */
  clear(): void {
    this.group.sendFieldCommand(this.field, 'clear');
  }

  unmount(): void {
    this.mountSeq++;
    this.installments?.reset();
    this.session.unmount();
  }
}
